<template>
  <UIPopup :closeable="false" :max-width="isDesktop ? '550px' : isTablet ? '550px' : ''" body-class="!mb-0">
    <template #header>
      <div class="flex w-full justify-end">
        <UIIcon name="close" size="lg" class="cursor-pointer transition-opacity" @click="cleanPopup()" />
      </div>
      <h3 class="mt-10 text-2xl text-black-100">Enter verification code</h3>
    </template>

    <template #body>
      <div class="mx-auto flex max-w-[486px] flex-col gap-2 mb-4">
        <div class="flex flex-col gap-2">
          <div class="flex flex-row justify-center gap-2 text-base">
            <span>We've sent a code to</span>
            <span class="font-bold">{{ hiddenPhone }}</span>
          </div>
        </div>

        <div class="flex flex-col items-center gap-2">
          <div class="flex flex-row justify-center gap-2">
            <input
              v-for="(__, index) in code"
              :key="index"
              type="number"
              v-model="code[index]"
              :ref="(el) => (refs[`code_${index}`].value = el as HTMLInputElement)"
              class="h-[64px] w-[48px] rounded-lg border-2 border-black-20 text-center text-[20px] leading-[84px] [appearance:textfield] tablet:h-[72px] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              placeholder=""
              maxlength="1"
              @input="focusOnNextInput($event, index)"
              @keydown="goBackOnBackspace($event, index)"
            />
          </div>

          <div
            v-if="isCodeInvalid"
            class="my-4 rounded-xl bg-additional-salmon-20 p-4 max-w-[330px] w-full text-center"
          >
            <h5 class="text-subhead-4 text-additional-bordo-100">Verification code is incorrect</h5>
          </div>
        </div>
      </div>
    </template>

    <template #buttons>
      <div class="flex flex-col gap-4 w-full max-w-[330px] mx-auto mb-10">
        <UIButton
          id="verify-phone-continue"
          class="w-full !font-bold"
          :disabled="isCodeInvalid || code.some((c) => c.length !== 1)"
          :loading="isPending"
          @click="handleVerify"
          >Continue</UIButton
        >
        <UIButton id="resend-code" variant="ghost" class="!text-caption-1" :disabled="resendTimer > 0" @click="resend">
          Resend code {{ resendTimer > 0 ? `in ${resendTimer}s` : '' }}
        </UIButton>
      </div>
    </template>
  </UIPopup>
</template>

<script setup lang="ts">
import type { UpdateUserProfileDTO } from '~/types'
const { isTablet, isDesktop } = useAppBreakpoints()

const { cleanPopup } = useUIStore()

const props = defineProps<{
  phone: { number: number; country: any }
  values: UpdateUserProfileDTO
}>()

const hiddenPhone = computed(() => {
  const phone = `${props.phone.country?.phone_code} ${props.phone.number}`
  const firstPart = phone.substring(0, 4)
  const lastPart = phone.substring(phone.length - 3)
  const middlePart = '*'.repeat(Math.max(0, phone.length - 7))
  return firstPart + middlePart + lastPart
})
const refs = {
  code_0: ref<HTMLInputElement | null>(null),
  code_1: ref<HTMLInputElement | null>(null),
  code_2: ref<HTMLInputElement | null>(null),
  code_3: ref<HTMLInputElement | null>(null),
  code_4: ref<HTMLInputElement | null>(null),
  code_5: ref<HTMLInputElement | null>(null),
}
const code = ref(['', '', '', '', '', ''])
const isCodeInvalid = ref(false)
const resendTimer = ref(0)
const isPending = ref(false)

const { mutateAsync: sendCode } = useSendSpecificVerifyCode()
const { mutateAsync: update } = useUpdateProfile()
const { mutateAsync: verify } = useVerifyOTP()
const { catchErrors } = useErrorHandler()

const focusOnNextInput = (event: Event, index: number) => {
  isCodeInvalid.value = false
  code.value[index] = (event.target as HTMLInputElement).value.slice(-1)

  if (code.value[index] && index < 5) {
    const nextInput = refs[`code_${index + 1}` as keyof typeof refs].value
    nextInput?.focus()
  }
}

const goBackOnBackspace = (event: KeyboardEvent, index: number) => {
  if (event.key === 'Backspace') {
    if (code.value[index]) {
      code.value[index] = ''
    } else if (index > 0) {
      refs[`code_${index - 1}` as keyof typeof refs].value?.focus()
    }
  }
}

const handleVerify = async () => {
  isPending.value = true
  isCodeInvalid.value = false

  try {
    const codeString = code.value.join('')
    await verify({
      phone: props.phone,
      code: codeString,
    })

    await update(props.values)
    useUIStore().showSnackBanner('success', 'Phone updated successfully')
    cleanPopup()
  } catch (error) {
    isCodeInvalid.value = true
    catchErrors(error)
  } finally {
    isPending.value = false
  }
}

const resend = () => {
  sendCode(props.phone)
  code.value = ['', '', '', '', '', '']
  refs.code_0.value?.focus()
  resendTimer.value = 30
  const interval = setInterval(() => {
    resendTimer.value--
    if (resendTimer.value === 0) clearInterval(interval)
  }, 1000)
}
</script>
